import { createPortal } from "react-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
} from "@tanstack/react-router";
import { WifiOff, AlertTriangle, Bug, RotateCcw, Home } from "lucide-react";

import { AuthProvider } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Layout } from "@/components/layout/Layout";
import { Toaster } from "@/components/ui/sonner";

export function PendingComponent() {
  return (
    <div className="fixed inset-x-0 top-0 z-[100] h-1 bg-muted overflow-hidden">
      <div className="h-full bg-primary animate-[loading-bar_1.5s_infinite_linear]" />
    </div>
  );
}

export function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Error classification ──────────────────────────────────── */

type ErrorKind = "network" | "server" | "crash";

function classifyError(error: Error): ErrorKind {
  const msg = error.message.toLowerCase();
  if (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network") ||
    msg.includes("abort") ||
    msg.includes("timeout") ||
    msg.includes("connect")
  ) {
    return "network";
  }
  if (
    msg.includes("500") ||
    msg.includes("internal server error") ||
    msg.includes("service unavailable") ||
    msg.includes("bad gateway") ||
    msg.includes("http 5")
  ) {
    return "server";
  }
  return "crash";
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const kind = classifyError(error);

  const config = {
    network: {
      icon: WifiOff,
      title: "Can't connect to our servers",
      description: "Check your internet connection, then try again.",
      color: "text-amber-500",
      bg: "bg-amber-50",
    },
    server: {
      icon: AlertTriangle,
      title: "We're having trouble loading this",
      description: "Something went wrong on our end. Try again in a moment.",
      color: "text-red-500",
      bg: "bg-red-50",
    },
    crash: {
      icon: Bug,
      title: "This page had a problem",
      description: "An unexpected error occurred. We've logged it for review.",
      color: "text-rose-500",
      bg: "bg-rose-50",
    },
  }[kind];

  const Icon = config.icon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className={`mx-auto mb-6 flex size-16 items-center justify-center rounded-full ${config.bg}`}>
          <Icon className={`size-8 ${config.color}`} />
        </div>
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {config.title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {config.description}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <RotateCcw className="size-4" />
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            <Home className="size-4" />
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { title: "OyoBooking — Premium Hotels & Event Venues in Oyo State" },
      {
        name: "description",
        content:
          "Discover, compare and book the finest hotels, event centres and open spaces across Oyo State, Nigeria.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "OyoBooking" },
      { property: "og:title", content: "OyoBooking — Premium Hotels & Event Venues in Oyo State" },
      {
        property: "og:description",
        content:
          "Discover, compare and book the finest hotels, event centres and open spaces across Oyo State, Nigeria.",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://oyobooking.ng/" }],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  pendingComponent: PendingComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  return (
    <AuthProvider>
      <FavoritesProvider>
        {typeof document !== "undefined" && createPortal(<HeadContent />, document.head)}
        <Layout>
          <Outlet />
        </Layout>
        <Toaster richColors position="top-center" />
      </FavoritesProvider>
    </AuthProvider>
  );
}
